// Payment Setup — the guided, lay-person bank/ACH setup surface shared by the
// Buyer, Seller and Broker portals. Party-scoped reads + writes over the new
// /api/portal/{buyer,seller,broker}/payment-setup endpoints (the backend agent
// owns the server side). No React Query in the portal — callers read with
// useState and re-run the loader after a mutation.

import { portalApi } from "./client";

// ── Buyer (payer) ────────────────────────────────────────────────────────────

/** Where ONE boarded loan is in bank-connect setup (Track C). The pre-loan-only
 *  `captured_preloan` state is NOT here — it lives on the overview's
 *  `preloanCaptured` (a single global platform capture, not per-loan).
 *  - `not_started` / `in_progress` — the loan's servicer-account mandate isn't
 *    confirmed yet;
 *  - `reauthorize_needed` — the loan boarded onto a real servicer: the buyer
 *    must re-authorize their bank directly with the servicer;
 *  - `servicer_changed` — the buyer connected their bank for a servicer that has
 *    since changed; they must reconnect for the new one (they did nothing wrong);
 *  - `pending_servicer` — the loan boarded but the servicer's payment account
 *    isn't ready; nothing for the buyer to do (auto-resolves);
 *  - `complete` — a bank mandate is on file for this loan's payments. */
export type BuyerLoanPaymentSetupStatus =
  | "not_started"
  | "in_progress"
  | "reauthorize_needed"
  | "servicer_changed"
  | "pending_servicer"
  | "complete";

/** One boarded loan's bank-mandate setup row. */
export interface BuyerLoanPaymentSetup {
  /** The loan this bank account is (or will be) connected to. */
  loanId: string;
  /** The licensed servicer (TPS) that securely holds the bank details. */
  tpsName: string | null;
  status: BuyerLoanPaymentSetupStatus;
  /** A chargeable mandate exists on THIS loan's servicer account — a pre-loan
   *  platform capture never sets this. */
  mandateOnFile: boolean;
  bankName: string | null;
  accountLast4: string | null;
  /** Why a boarded loan is still pending: "reauthorize" | "servicer_onboarding". */
  pendingReason: string | null;
}

/** Where ONE in-progress deal (no loan yet) is in pre-loan bank capture.
 *  - `not_started` — nothing captured for this deal yet;
 *  - `in_progress` — a capture is open/pending;
 *  - `captured_preloan` — a pre-loan bank is saved on the platform for this deal
 *    (no servicer assigned yet — it will need re-authorization at boarding);
 *  - `complete` — the bank is saved DIRECTLY on the deal's assigned servicer's
 *    account, so it carries forward as final with nothing more to do at boarding;
 *  - `servicer_changed` — the buyer connected for a servicer that has since
 *    changed; they must reconnect for the new one. */
export type BuyerDealPaymentSetupStatus =
  | "not_started"
  | "in_progress"
  | "captured_preloan"
  | "complete"
  | "servicer_changed";

/** One in-progress deal's pre-loan bank state. Each row drives its OWN
 *  deal-scoped {@link buyerPaymentSetupApi.beginPreloan}(dealId). */
export interface BuyerDealPaymentSetup {
  /** The in-progress deal this pre-loan bank capture belongs to. */
  dealId: string;
  dealNumber: string | null;
  businessName: string | null;
  /** Buyer-friendly rendering of the deal's pipeline stage. */
  stageLabel: string;
  status: BuyerDealPaymentSetupStatus;
  /** The captured bank's last-4 when captured (`captured_preloan` / `complete`). */
  accountLast4: string | null;
  /** True when the deal already has a real, ready servicer, so connecting a bank
   *  here saves it DIRECTLY on that servicer's account — final, with no
   *  re-authorization at boarding. Drives the connect copy. */
  connectsToServicer: boolean;
}

/** The buyer "Payment setup" tab — the multi-loan overview. One
 *  {@link BuyerLoanPaymentSetup} per boarded loan, plus one
 *  {@link BuyerDealPaymentSetup} per in-progress deal (no loan yet) and the
 *  single global pre-loan platform capture. Both `loans` and `deals` are empty
 *  when there is nothing to set up yet. */
export interface BuyerPaymentSetupOverview {
  /** The buyer saved a bank pre-loan (platform capture — display only). */
  preloanCaptured: boolean;
  /** The saved pre-loan bank's last-4 (only when `preloanCaptured`). */
  preloanBankLast4: string | null;
  loans: BuyerLoanPaymentSetup[];
  /** One pre-loan bank-capture row per in-progress deal (no loan yet). */
  deals: BuyerDealPaymentSetup[];
}

/** What the pre-loan bank-connect mounts the Payment Element with. `stripeAccount`
 *  is the TPS clearing (connected) account when the deal already has a real, ready
 *  servicer (a CONNECTED capture that carries forward as final); it is `null` for
 *  the PLATFORM fallback (no servicer yet — capture/display only). The FE mounts
 *  `loadStripe(pk, stripeAccount ?? undefined)`. */
export interface BuyerPreloanMandateBegin {
  setupIntentId: string;
  clientSecret: string;
  publishableKey: string;
  stripeAccount: string | null;
}

export const buyerPaymentSetupApi = {
  /** The buyer's bank-connect overview: one row per boarded loan, one row per
   *  in-progress deal, + the single global pre-loan platform capture. */
  get: () => portalApi.get<BuyerPaymentSetupOverview>("/buyer/payment-setup"),
  /** Begin (or resume) the pre-loan platform bank capture for a specific deal. */
  beginPreloan: (dealId: string) =>
    portalApi.post<BuyerPreloanMandateBegin>(
      `/buyer/payment-setup/preloan/begin?dealId=${encodeURIComponent(dealId)}`,
    ),
  /** Post-confirm sync (for a deal) — stamps the capture and returns the
   *  refreshed overview. */
  syncPreloan: (dealId: string) =>
    portalApi.post<BuyerPaymentSetupOverview>(
      `/buyer/payment-setup/preloan/sync?dealId=${encodeURIComponent(dealId)}`,
    ),
};

// ── Seller / Broker (payee) ──────────────────────────────────────────────────

/** Where a payee (seller / broker) is in payout-account onboarding.
 *  - `not_started` — no Stripe payout account yet;
 *  - `in_progress` — onboarding started but Stripe still needs details;
 *  - `complete` — the account is active and can receive payouts. */
export type PayeePaymentSetupStatus = "not_started" | "in_progress" | "complete";

/** The Stripe Connect account's own readiness. */
export type PayeeAccountStatus = "none" | "pending" | "restricted" | "active";

export interface PayeePaymentSetup {
  status: PayeePaymentSetupStatus;
  accountStatus: PayeeAccountStatus;
  bankName: string | null;
  accountLast4: string | null;
}

/** A link into Stripe's hosted onboarding (itself a guided, compliant flow). */
export interface PayeeOnboardingLink {
  url: string;
}

/** The generic shape both the Seller and Broker panels drive so the shared
 *  {@link PayeePaymentSetupPanel} stays account-type-agnostic. */
export interface PayeePaymentSetupApi {
  get: () => Promise<PayeePaymentSetup>;
  /** Begin (or resume) Stripe hosted onboarding; the browser navigates to `url`. */
  onboardingLink: () => Promise<PayeeOnboardingLink>;
  /** Re-check the account's readiness after returning from Stripe. */
  refresh: () => Promise<PayeePaymentSetup>;
}

export const sellerPaymentSetupApi: PayeePaymentSetupApi = {
  get: () => portalApi.get<PayeePaymentSetup>("/seller/payment-setup"),
  onboardingLink: () =>
    portalApi.post<PayeeOnboardingLink>("/seller/payment-setup/onboarding-link"),
  refresh: () => portalApi.post<PayeePaymentSetup>("/seller/payment-setup/refresh"),
};

export const brokerPaymentSetupApi: PayeePaymentSetupApi = {
  get: () => portalApi.get<PayeePaymentSetup>("/broker/payment-setup"),
  onboardingLink: () =>
    portalApi.post<PayeeOnboardingLink>("/broker/payment-setup/onboarding-link"),
  refresh: () => portalApi.post<PayeePaymentSetup>("/broker/payment-setup/refresh"),
};
